import { useEffect, useState } from 'react';
import GraficoBarras from './GraficoBarras';
import GraficoLinhaDuploEixo from './GraficoLinhaDuploEixo';
import GraficoLinha from './GraficoLinhas';
import GraficoLinhasMarcoMulti from './GraficoLinhasMarco';
import { chartsConf, chartsConfigData } from '../../../services/api/charts/charts';
import { AlertColor } from '@mui/material';

type Instrumento = {
	leituras: any[];
	nome: string;
	tipo_instrumento: string;
	limite_faixa_verde: string | null;
	limite_faixa_amarela: string | null;
	limite_faixa_laranja: string | null;
	limite_faixa_vermelha: string | null;
	limite_faixa_verde_y: string | null;
	limite_faixa_verde_z: string | null;
	limite_faixa_amarela_y: string | null;
	limite_faixa_amarela_z: string | null;
	limite_faixa_laranja_y: string | null;
	limite_faixa_laranja_z: string | null;
	limite_faixa_vermelha_y: string | null;
	limite_faixa_vermelha_z: string | null;
	uuid: string;
};

type DadosInstrumento = {
	nome: string;
	leituras: any[];
	corLinha: string;
	yKey: string;
	limites: {
		verde: number | null;
		amarelo: number | null;
		laranja: number | null;
		vermelho: number | null;
	};
};

type Props = {
	instrumentos: Instrumento[];
	tipoSelecionado: string;
	user_id: string;
	barragem_id: string;
	alert: (menssage: string, severity: AlertColor) => void;
};

const CORES = [
	'#007bff',
	'#dc3545',
	'#ffc107',
	'#28a745',
	'#6f42c1',
	'#fd7e14',
	'#20c997',
];

const toNullableNumber = (value: string | null) => {
	if (value === null || value === '') {
		return null;
	}

	const parsed = Number(value);
	return Number.isNaN(parsed) ? null : parsed;
};

const ContainerGraficos = ({ instrumentos, tipoSelecionado, barragem_id, user_id, alert }: Props) => {

	const instrumentosFiltrados = instrumentos.filter(
		(inst) => inst.tipo_instrumento === tipoSelecionado && inst.leituras.length > 0
	);

	const instrumentosCota = instrumentos.filter(instr =>
		['piezometro', 'indicador', 'regua', 'poco'].includes(instr.tipo_instrumento)
	);

	const getYKeyByTipo = (tipo: string): string => {
		switch (tipo) {
			case 'pluviometro':
				return 'leitura_diaria';
			case 'piezometro':
				return 'cota';
			case 'regua':
				return 'cota';
			case 'indicador':
				return 'cota';
			case 'vazao':
				return 'vazao';
			case 're':
				return 'angulo';
			case 'inclinometro':
				return 'deslocamento';
			case 'poco':
				return 'cota';
			default:
				return 'teste';
		}
	};

	const instrumentosFormatados: DadosInstrumento[] = instrumentosFiltrados.map(
		(instr, idx) => ({
			nome: instr.nome,
			leituras: instr.leituras,
			corLinha: CORES[idx % CORES.length],
			yKey: getYKeyByTipo(instr.tipo_instrumento),
			limites: {
				verde: toNullableNumber(instr.limite_faixa_verde),
				amarelo: toNullableNumber(instr.limite_faixa_amarela),
				laranja: toNullableNumber(instr.limite_faixa_laranja),
				vermelho: toNullableNumber(instr.limite_faixa_vermelha),
			},
		})
	);

	const instrumentosFormatadosBarra = instrumentosFiltrados.map(inst => ({
		nome: inst.nome,
		leituras: inst.leituras.map(l => ({
			data_leitura: l.data_leitura,
			leitura_diaria: Number(l.leitura_diaria),
		})),
		corBarra: '#007bff',
		limites: {
			verde: toNullableNumber(inst.limite_faixa_verde),
			amarelo: toNullableNumber(inst.limite_faixa_amarela),
			laranja: toNullableNumber(inst.limite_faixa_laranja),
			vermelho: toNullableNumber(inst.limite_faixa_vermelha),
		}
	}));

	const instrumentosFormatadosMacro: any[] = instrumentosFiltrados.map((instr, idx) => ({
		nome: instr.nome,
		leituras: instr.leituras,
		corLinha: CORES[idx % CORES.length],
		limites: {
			x: [
				toNullableNumber(instr.limite_faixa_verde),
				toNullableNumber(instr.limite_faixa_amarela),
				toNullableNumber(instr.limite_faixa_laranja),
				toNullableNumber(instr.limite_faixa_vermelha),
			],
			y: [
				toNullableNumber(instr.limite_faixa_verde_y),
				toNullableNumber(instr.limite_faixa_amarela_y),
				toNullableNumber(instr.limite_faixa_laranja_y),
				toNullableNumber(instr.limite_faixa_vermelha_y),
			],
			z: [
				toNullableNumber(instr.limite_faixa_verde_z),
				toNullableNumber(instr.limite_faixa_amarela_z),
				toNullableNumber(instr.limite_faixa_laranja_z),
				toNullableNumber(instr.limite_faixa_vermelha_z),
			],
		},
	}));

	const instrumentosFormatadosCota: any[] = instrumentosCota.map((instr, idx) => ({
		nome: instr.nome,
		leituras: instr.leituras,
		tipo: instr.tipo_instrumento,
		corLinha: CORES[idx % CORES.length],
		yKey: getYKeyByTipo(instr.tipo_instrumento),
		limites: {
			verde: toNullableNumber(instr.limite_faixa_verde),
			amarelo: toNullableNumber(instr.limite_faixa_amarela),
			laranja: toNullableNumber(instr.limite_faixa_laranja),
			vermelho: toNullableNumber(instr.limite_faixa_vermelha),
		}
	}));

	const [confGrafico, setConfGrafico] = useState<chartsConfigData[] | null>(null);

	async function fetchDataConfig(id_barragem: string, id_user: string) {
		try {
			const res = await chartsConf.get(id_barragem, id_user);
			if (res instanceof Error) {
				alert('Erro ao carregar configurações do gráfico!', 'error');
			} else {
				if (res.length > 0) {
					const data = res.filter((index: { tipo_grafico: string; }) => index.tipo_grafico === tipoSelecionado);
					if (data.length > 0) {
						setConfGrafico(data);
					} else {
						setConfGrafico(null);
					}
				}
			}
		} catch (error) {
			alert('Erro ao carregar configurações do gráfico!', 'error');
		}
	}

	useEffect(() => {
		fetchDataConfig(barragem_id, user_id);
	}, [tipoSelecionado]);

	return (
		<div className="grid grid-cols-1 gap-6">
			{tipoSelecionado === 'pluviometro' ? (
				<GraficoBarras instrumentos={instrumentosFormatadosBarra} barragem_id={barragem_id} tipo_instrumento={tipoSelecionado} confGrafico={confGrafico} alert={alert} />
			) : tipoSelecionado === 'marco' ? (
				<GraficoLinhasMarcoMulti instrumentos={instrumentosFormatadosMacro} barragem_id={barragem_id} tipo_instrumento={tipoSelecionado} confGrafico={confGrafico} alert={alert} />
			) : tipoSelecionado === 'cota' ? (
				<GraficoLinhaDuploEixo instrumentos={instrumentosFormatadosCota} barragem_id={barragem_id} tipo_instrumento={tipoSelecionado} confGrafico={confGrafico} alert={alert} />
			) :
				(
					<GraficoLinha instrumentos={instrumentosFormatados} legendaY={getYKeyByTipo(tipoSelecionado)} barragem_id={barragem_id} tipo_instrumento={tipoSelecionado} confGrafico={confGrafico} alert={alert} />
				)}
		</div>
	);
};

export default ContainerGraficos;
